const defaultState = {
  items: [],
  totalRows: 0,
  fetching: false,
  fetched: false,
  error: null,
  // Keyed cache of previously-fetched pages (see src/actions/getUsersList.js).
  cache: {},
  // Set when a write invalidates the cache; makes the next fetch send refresh=true
  // so the Lambda also busts its warm-container scan cache. Cleared once sent.
  bustBackend: false
};

// Hard cap on cached pages so the cache can't grow unbounded over a long session
// (each entry is one page, a few KB). When exceeded, the oldest entry is dropped.
const MAX_CACHE_ENTRIES = 50;

const putInCache = (cache, key, entry) => {
  const next = { ...cache, [key]: entry };
  const keys = Object.keys(next);
  if (keys.length > MAX_CACHE_ENTRIES) {
    const oldest = keys.reduce((a, b) => (next[a].fetchedAt <= next[b].fetchedAt ? a : b));
    delete next[oldest];
  }
  return next;
};

// Writes that can change who appears in the user list (new/edited user, or a
// location assignment change). When one succeeds we drop the whole page cache so
// the next fetch re-queries and freshly changed users show immediately.
const INVALIDATE_ON = new Set([
  'ADD_USER_FETCH_FULFILLED',
  'UPDATE_USER_FETCH_FULFILLED',
  'PERSON_SAVE_FULFILLED',
  'UPDATE_PERSON_SAVE_FULFILLED',
  'ADD_EDIT_LOCATION_SAVE_FULFILLED',
  'ADD_EDIT_CUSTOMER_SAVE_FULFILLED'
]);

const usersListReducer = (state = defaultState, action) => {
  if (INVALIDATE_ON.has(action.type)) {
    return { ...state, cache: {}, bustBackend: true };
  }

  switch (action.type) {
    case 'USERS_LIST_FETCH_PENDING':
      return { ...state, fetching: true, error: null };
    case 'USERS_LIST_FETCH_FULFILLED': {
      const { items, totalRows } = action.payload.data;
      const key = action.meta && action.meta.key;
      // Cache-hit replays carry the original fetchedAt so the TTL doesn't slide.
      const fetchedAt = action.payload.data.fetchedAt || Date.now();
      return {
        ...state,
        fetching: false,
        fetched: true,
        error: null,
        items,
        totalRows,
        cache: key ? putInCache(state.cache, key, { items, totalRows, fetchedAt }) : state.cache,
        // Clear the bust only once the refresh request actually succeeds, so a
        // failed one stays flagged and the retry still sends refresh=true.
        bustBackend: action.meta && action.meta.bust ? false : state.bustBackend,
      };
    }
    // REJECTED intentionally leaves bustBackend set so the retry re-sends refresh.
    case 'USERS_LIST_FETCH_REJECTED':
      return { ...state, fetching: false, error: action.payload };
    default:
      return state;
  }
};

export default usersListReducer;



// WEBPACK FOOTER //
// ./src/reducers/usersList.js